import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  useTheme,
  alpha,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tabs,
  Tab,
  Avatar,
  Rating,
  Stack
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import DescriptionIcon from "@mui/icons-material/Description";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import HistoryIcon from "@mui/icons-material/History";
import VerifiedIcon from "@mui/icons-material/Verified";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import TimelapseIcon from "@mui/icons-material/Timelapse";
import GavelIcon from "@mui/icons-material/Gavel";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import ShareIcon from "@mui/icons-material/Share";
import CloseIcon from "@mui/icons-material/Close";
import FeatureLayout from "./FeatureLayout";

// App screenshot placeholders
import incidentScreenshot1 from "../../../images/features/incident-1.png";
import incidentScreenshot2 from "../../../images/features/incident-1.png";
import incidentScreenshot3 from "../../../images/features/incident-1.png";

const IncidentReport = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [sampleReportOpen, setSampleReportOpen] = useState(false);

  const benefits = [
    {
      icon: <PhotoCameraIcon />,
      title: "Photo Documentation",
      description: "Cleaners upload time-stamped, geotagged photos of any damage found during pre-cleaning inspection.",
    },
    {
      icon: <VerifiedIcon />,
      title: "Tamper-Proof Evidence",
      description: "All reports are automatically timestamped and linked to the specific booking. Perfect for Airbnb claims.",
    },
    {
      icon: <NotificationsActiveIcon />,
      title: "Instant Alerts",
      description: "Get notified immediately when damage is discovered. Address issues before the next guest arrives.",
    },
    {
      icon: <DescriptionIcon />,
      title: "Professional Reports",
      description: "Generate downloadable PDF reports with all photos, descriptions, and booking details.",
    },
    {
      icon: <HistoryIcon />,
      title: "Complete History",
      description: "All incident reports are saved per property. Track recurring issues and problem areas.",
    },
    {
      icon: <GavelIcon />,
      title: "Airbnb-Ready Documentation",
      description: "Reports formatted specifically for Airbnb dispute resolution and insurance claims.",
    },
  ];

  const stats = [
    { value: "100%", label: "Tamper-proof documentation" },
    { value: "Instant", label: "Host notifications" },
    { value: "Airbnb-ready", label: "Dispute format" },
    { value: "Unlimited", label: "Photos per report" },
  ];

  const steps = [
    {
      title: "Cleaner Arrives",
      description: "Before starting the cleaning, cleaner does a walkthrough inspection",
      icon: "👋",
    },
    {
      title: "Document Damage",
      description: "Take time-stamped photos of any damage or missing items",
      icon: "📸",
    },
    {
      title: "Submit Report",
      description: "Add description and submit report through the app",
      icon: "📋",
    },
    {
      title: "Host Notified",
      description: "You receive instant notification with full details",
      icon: "🔔",
    },
    {
      title: "Take Action",
      description: "File claim, contact guest, or schedule repairs",
      icon: "✅",
    },
  ];

  // Sample report data for demo modal
  const sampleReport = {
    id: "IR-2024-0425",
    property: "Beachfront Villa - Unit 4B",
    bookingId: "ABC-123456",
    guestName: "Michael Thompson",
    checkIn: "April 25, 2024",
    checkOut: "April 28, 2024",
    reportedBy: "Maria Garcia (Cleaner)",
    reportedAt: "April 25, 2024 - 10:32 AM",
    items: [
      {
        name: "Coffee Table (Living Room)",
        damage: "Deep scratch on glass surface",
        location: "Living room, center area",
        estimatedRepair: "$150-200",
        photos: 3,
      },
      {
        name: "Kitchen Countertop",
        damage: "Burn mark near stove",
        location: "Kitchen, right side",
        estimatedRepair: "$300-400",
        photos: 2,
      },
      {
        name: "Bathroom Mirror",
        damage: "Cracked corner",
        location: "Master bathroom",
        estimatedRepair: "$120",
        photos: 2,
      },
    ],
    notes: "All damages appear to be from recent guest. Cleaner noted that previous inspection (April 20) showed no damage. Recommend filing claim through Airbnb Resolution Center.",
  };

  const handleOpenReport = () => setSampleReportOpen(true);
  const handleCloseReport = () => setSampleReportOpen(false);

  return (
    <FeatureLayout
      title="Incident Report"
      subtitle="Protect your property with tamper-proof damage documentation"
      description="Before every cleaning, cleaners perform a pre-inspection walkthrough. Any damage, missing items, or issues are immediately documented with geo-tagged, time-stamped photos. You get notified instantly and receive an Airbnb-ready report for dispute resolution."
      icon={<WarningIcon sx={{ fontSize: 32 }} />}
      iconGradient="linear-gradient(135deg, #ff6b6b, #ee5a24)"
      breadcrumbs={[
        { label: "Features", link: "/features" },
        { label: "Incident Report" },
      ]}
      benefits={benefits}
      stats={stats}
      imageRight={incidentScreenshot1}
      imageLeft={incidentScreenshot2}
    >
      {/* How It Works */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            mb: 4,
            textAlign: "center",
            fontSize: { xs: "1.8rem", md: "2.5rem" },
          }}
        >
          How Incident Reports Work
        </Typography>

        <Grid container spacing={3}>
          {steps.map((step, idx) => (
            <Grid item xs={12} sm={6} md={2.4} key={idx}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  textAlign: "center",
                  borderRadius: 3,
                  height: "100%",
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  transition: "transform 0.2s",
                  "&:hover": { transform: "translateY(-4px)" },
                }}
              >
                <Box sx={{ fontSize: "2.5rem", mb: 1 }}>{step.icon}</Box>
                <Typography variant="h6" fontWeight="700" gutterBottom>
                  {step.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {step.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Key Features */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            mb: 4,
            textAlign: "center",
            fontSize: { xs: "1.8rem", md: "2.5rem" },
          }}
        >
          Every Report Includes
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                height: "100%",
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "16px",
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                  color: theme.palette.error.main,
                }}
              >
                <LocationOnIcon sx={{ fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight="800" gutterBottom>
                Geo-Tagged Photos
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Every photo includes GPS coordinates, proving the damage was documented at your property. Essential for dispute resolution.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                height: "100%",
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "16px",
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                  color: theme.palette.info.main,
                }}
              >
                <AccessTimeIcon sx={{ fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight="800" gutterBottom>
                Time-Stamped Evidence
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Exact date and time of discovery. Proves damage occurred during specific guest's stay. No more "was it already there?" disputes.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                height: "100%",
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "16px",
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                  color: theme.palette.success.main,
                }}
              >
                <AssignmentIcon sx={{ fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight="800" gutterBottom>
                Booking-Linked Reports
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Every report is tied to a specific booking, guest, and property. Complete chain of custody for Airbnb claims.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Sample Report Preview */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Box
            sx={{
              p: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography variant="h5" fontWeight="800">
                Sample Incident Report
              </Typography>
              <Typography variant="body2" color="text.secondary">
                See exactly what hosts receive when damage is found
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<DescriptionIcon />}
              onClick={handleOpenReport}
              sx={{ borderRadius: "50px" }}
            >
              View Full Report
            </Button>
          </Box>

          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Sample Damage Entry
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    borderColor: alpha(theme.palette.error.main, 0.3),
                  }}
                >
                  <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        bgcolor: alpha(theme.palette.error.main, 0.1),
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <PhotoCameraIcon sx={{ fontSize: 32, color: theme.palette.error.main }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="700">
                        Coffee Table Scratch
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Deep scratch on glass surface - Living room
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                        <Chip size="small" label="3 photos" icon={<PhotoCameraIcon />} />
                        <Chip size="small" label="Est. $150-200 repair" />
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Report Metadata
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LocationOnIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                    <Typography variant="body2">Geotagged: 25.7617° N, 80.1918° W</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AccessTimeIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                    <Typography variant="body2">Timestamp: April 25, 2024 - 10:32 AM</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AssignmentIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                    <Typography variant="body2">Linked to Booking: #ABC-123456</Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>

      {/* Cleaner Workflow Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" fontWeight="800" gutterBottom>
              Cleaner's App View
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
              Before starting any cleaning, cleaners are prompted to complete a pre-inspection walkthrough. The app guides them through each room to document existing conditions.
            </Typography>
            <List>
              {[
                "Step-by-step room-by-room inspection guide",
                "Quick photo capture with auto-organization",
                "Damage severity rating (Minor/Moderate/Severe)",
                "Add notes and descriptions for each item",
                "Submit report with one tap - host notified instantly",
              ].map((text, idx) => (
                <ListItem key={idx}>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: theme.palette.success.main }} />
                  </ListItemIcon>
                  <ListItemText primary={text} />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={incidentScreenshot3}
              alt="Cleaner app incident reporting interface"
              sx={{
                width: "100%",
                borderRadius: 4,
                boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Report Template Features */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            mb: 4,
            textAlign: "center",
            fontSize: { xs: "1.8rem", md: "2.5rem" },
          }}
        >
          Professional Report Features
        </Typography>

        <Grid container spacing={3}>
          {[
            { icon: <DownloadIcon />, title: "PDF Export", desc: "Download professional reports for insurance or legal use" },
            { icon: <PrintIcon />, title: "Print Ready", desc: "Print reports directly for your records or property files" },
            { icon: <ShareIcon />, title: "Share with Repair Services", desc: "Send damage reports directly to handymen or restoration companies" },
            { icon: <HistoryIcon />, title: "Damage History", desc: "Track recurring issues across multiple stays" },
          ].map((item, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  textAlign: "center",
                  borderRadius: 3,
                  height: "100%",
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <Box sx={{ color: theme.palette.primary.main, mb: 2 }}>
                  {React.cloneElement(item.icon, { sx: { fontSize: 32 } })}
                </Box>
                <Typography variant="h6" fontWeight="700" gutterBottom>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.desc}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Integration Note */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.05)} 0%, ${alpha(theme.palette.info.dark, 0.02)} 100%)`,
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h5" fontWeight="800" gutterBottom>
                🔗 Works with Airbnb Resolution Center
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Incident reports are formatted specifically for Airbnb's dispute resolution process. 
                Include them directly in your claim to strengthen your case.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
              <Chip
                label="Airbnb Approved Format"
                sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}
              />
            </Grid>
          </Grid>
        </Paper>
      </Container>

      {/* Sample Report Modal */}
      <Dialog
        open={sampleReportOpen}
        onClose={handleCloseReport}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography variant="h6" fontWeight="800">Incident Report</Typography>
              <Typography variant="caption" color="text.secondary">{sampleReport.id}</Typography>
            </Box>
            <IconButton onClick={handleCloseReport}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Property Information</Typography>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Property</Typography>
                  <Typography variant="body2" fontWeight="500">{sampleReport.property}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Booking ID</Typography>
                  <Typography variant="body2" fontWeight="500">{sampleReport.bookingId}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Guest</Typography>
                  <Typography variant="body2" fontWeight="500">{sampleReport.guestName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Stay Dates</Typography>
                  <Typography variant="body2" fontWeight="500">{sampleReport.checkIn} - {sampleReport.checkOut}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Report Metadata</Typography>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Reported By</Typography>
                  <Typography variant="body2" fontWeight="500">{sampleReport.reportedBy}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Date & Time</Typography>
                  <Typography variant="body2" fontWeight="500">{sampleReport.reportedAt}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Damages Reported</Typography>
            {sampleReport.items.map((item, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="700">{item.name}</Typography>
                  <Chip size="small" label={`${item.photos} photos`} icon={<PhotoCameraIcon />} />
                </Box>
                <Typography variant="body2" color="error.main" gutterBottom>
                  {item.damage}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Location: {item.location}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip size="small" label={`Est. Repair: ${item.estimatedRepair}`} variant="outlined" />
                </Box>
              </Paper>
            ))}
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Additional Notes</Typography>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
              <Typography variant="body2">{sampleReport.notes}</Typography>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button startIcon={<DownloadIcon />} variant="outlined">
            Download PDF
          </Button>
          <Button startIcon={<PrintIcon />} variant="outlined">
            Print
          </Button>
          <Button startIcon={<ShareIcon />} variant="outlined">
            Share
          </Button>
        </DialogActions>
      </Dialog>
    </FeatureLayout>
  );
};

export default IncidentReport;